import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FileText, Info, Loader2, Split } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { joApi, joErr, sendToText } from '@/services/processingJoApi';
import { JoBadge, JoChips, OverdueBadge, fmtDay, fmtN } from './JoBits';
import { JoSlipsTable } from './JoTables';
import { ArticleRow, NqTiles } from './GsArticleRows';
import { rowSum, useColourQty } from './useColourQty';

const pk = (sort, c) => `${sort}|${c}`;

/** Pieces each stock pool (sort + colour) is already given on every row — a parent can be shared by several rows. */
const poolUsage = (arts, qty) => {
  const u = {};
  const add = (sort, m) => Object.entries(m || {}).forEach(([c, n]) => { u[pk(sort, c)] = (u[pk(sort, c)] || 0) + (Number(n) || 0); });
  arts.forEach((a) => { add(a.sort_no, qty[a.sort_no]?.own); if (a.parent) add(a.parent.sort_no, qty[a.sort_no]?.parent); });
  return u;
};

const availFor = (a, row, usage) => {
  const left = (sort, src) => (c) => ({ ...c, left: c.free_pcs - ((usage[pk(sort, c.color_name)] || 0) - (row?.[src]?.[c.color_name] || 0)) });
  return { own: a.colors.map(left(a.sort_no, 'own')), parent: (a.parent?.colors || []).map(left(a.parent?.sort_no, 'parent')) };
};

const splitPayload = (avail) => ({
  own: avail.own.map((c) => ({ color_name: c.color_name, free_pcs: Math.max(0, c.left) })),
  parent: avail.parent.map((c) => ({ color_name: c.color_name, free_pcs: Math.max(0, c.left) })),
});

const entries = (m) => Object.entries(m || {}).filter(([, n]) => n > 0).map(([color_name, pieces]) => ({ color_name, pieces }));
const slipLines = (rows, qty) => rows.map((a) => ({ sort_no: a.sort_no, pieces: rowSum(qty[a.sort_no]),
  colors: entries(qty[a.sort_no]?.own), parent_colors: a.parent ? entries(qty[a.sort_no]?.parent) : [] }));

const ArticlesTable = ({ arts, cq, usage, open, setOpen, disabled }) => (
  <div className="overflow-x-auto rounded-md border bg-white">
    <Table data-testid="gs-jo-articles-table">
      <TableHeader><TableRow><TableHead className="w-8" /><TableHead>Base article</TableHead><TableHead className="text-right">Free bundles</TableHead><TableHead className="text-right">Free pcs</TableHead><TableHead>Pieces</TableHead></TableRow></TableHeader>
      <TableBody>{arts.map((a) => {
        const row = cq.qty[a.sort_no];
        const avail = availFor(a, row, usage);
        return (
          <ArticleRow key={a.sort_no} a={a} avail={avail} row={row} open={!!open[a.sort_no]} total={cq.typed[a.sort_no]} splitting={!!cq.splitting[a.sort_no]}
            disabled={disabled} onToggle={() => setOpen((p) => ({ ...p, [a.sort_no]: !p[a.sort_no] }))}
            onTotal={(v) => { cq.setTotal(a, v, splitPayload(avail)); setOpen((p) => ({ ...p, [a.sort_no]: true })); }}
            onColour={(src, c, v) => cq.setColour(a, src, c.color_name, v, c.left)} />
        );
      })}</TableBody>
    </Table>
  </div>
);

const PlanActions = ({ total, balance, mixed, canPlan, busy, disabled, onGenerate }) => (
  <div className="flex flex-wrap items-center gap-3">
    <span className={`text-sm ${total > balance ? 'text-rose-700 font-semibold' : 'text-gray-600'}`} data-testid="gs-jo-plan-total">{fmtN(total)} / {fmtN(balance)} pcs balance</span>
    {!canPlan ? <span className="text-xs text-amber-700 ml-auto" data-testid="gs-jo-plan-locked">Only a Supervisor / In-charge makes pick slips</span> : (
      <Button onClick={onGenerate} disabled={disabled} className="bg-[#2D5F4F] hover:bg-[#234A3D] ml-auto" data-testid={mixed ? 'gs-jo-make-two-btn' : 'gs-jo-generate-btn'}>
        {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : mixed ? <Split className="w-4 h-4 mr-2" /> : <FileText className="w-4 h-4 mr-2" />}
        {mixed ? 'Make 2 slips (non-NQ + NQ)' : 'Generate pick slip'}</Button>
    )}
  </div>
);

/** Greige Storage: base articles of the job order's FG in stock (own or parent sort) → colour-wise pieces → pick slip(s). */
export const GsPlanPanel = ({ order, canPlan, onSlip, onChanged }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const cq = useColourQty();
  const { reset } = cq;
  const load = useCallback(() => joApi.articles(order.jo_id).then(setData).catch((e) => toast.error(joErr(e, 'Could not load base articles'))), [order.jo_id]);
  useEffect(() => { reset(); setOpen({}); setData(null); load(); }, [load, reset]);
  const arts = useMemo(() => data?.articles || [], [data]);
  const usage = useMemo(() => poolUsage(arts, cq.qty), [arts, cq.qty]);
  const entered = arts.filter((a) => rowSum(cq.qty[a.sort_no]) > 0);
  const total = entered.reduce((s, a) => s + rowSum(cq.qty[a.sort_no]), 0);
  const mixed = new Set(entered.map((a) => a.is_nq)).size > 1;

  const generate = async () => {
    setBusy(true);
    const made = [];
    try {
      for (const g of (mixed ? [entered.filter((a) => !a.is_nq), entered.filter((a) => a.is_nq)] : [entered])) {
        made.push(await joApi.createSlip(order.jo_id, slipLines(g, cq.qty)));
      }
      toast.success(made.length > 1 ? `Pick slips ${made.map((s) => s.slip_id).join(' + ')} made` : `Pick slip ${made[0].slip_id} made — ${made[0].bundles.length} bundle(s)`);
    } catch (e) {
      toast.error(made.length ? `${made[0].slip_id} made, but the NQ slip failed: ${joErr(e)}` : joErr(e, 'Could not make the pick slip'));
    } finally {
      setBusy(false);
      if (made.length) { reset(); setOpen({}); load(); onChanged(); onSlip(made[0]); }
    }
  };

  return (
    <Card data-testid="gs-jo-plan-panel">
      <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3">
        <div className="min-w-0"><CardTitle className="text-base font-mono" data-testid="gs-jo-plan-id">{order.jo_id}</CardTitle>
          <p className="text-sm"><span className="font-mono">{order.fg_name}</span> <span className="text-gray-600">{[order.design_name, order.shade].filter(Boolean).join(' · ')}</span></p>
          <p className="text-xs text-gray-500">To {sendToText(order)}{order.target_date ? ` · expected ${fmtDay(order.target_date)}` : ''} · by {order.created_by_name}</p></div>
        <div className="flex gap-1"><OverdueBadge days={order.days_late} testId="gs-jo-plan-overdue" /><JoBadge status={order.stage} testId="gs-jo-plan-status" /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        {data && <NqTiles data={data} />}
        <JoChips o={order} testPrefix="gs-jo" />
        {order.status === 'OPEN' && order.balance_to_send > 0 && (
          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-800">Base articles in stock — enter pieces to send now</p>
            {!data ? <Loader2 className="w-5 h-5 animate-spin mx-auto my-6 text-[#2D5F4F]" />
              : !arts.length ? <p className="text-sm text-gray-500 rounded-md border bg-white px-3 py-6 text-center" data-testid="gs-jo-articles-empty">No base article of {order.fg_name} (or its parent sort no) has free stock in Greige Storage</p>
                : <ArticlesTable arts={arts} cq={cq} usage={usage} open={open} setOpen={setOpen} disabled={!canPlan} />}
            {mixed && (
              <p className="flex items-center gap-2 text-xs text-indigo-800 bg-indigo-50 border border-indigo-200 rounded p-2" data-testid="gs-jo-nq-mix-warning">
                <Info className="w-4 h-4 shrink-0" />NQ and non-NQ sort nos can't share one pick slip — "Make 2 slips" makes one for each</p>
            )}
            <PlanActions total={total} balance={order.balance_to_send} mixed={mixed} canPlan={canPlan} busy={busy}
              disabled={busy || cq.busy || !total || total > order.balance_to_send} onGenerate={generate} />
            <p className="text-[11px] text-gray-500">Type a row total and it splits by colour like the Assortment Pick Slip — the sort's own stock first, then its parent sort no; change any colour and the row total follows. Parent bundles change to the base article's sort no when picked. Whole bundles are suggested oldest first, so a slip can go slightly over.</p>
          </div>
        )}
        <div className="space-y-1"><p className="text-sm font-semibold text-gray-800">Pick slips of this order</p>
          <div className="rounded-md border bg-white"><JoSlipsTable slips={order.slips || []} onOpen={onSlip} testPrefix="gs-jo-slip" /></div></div>
      </CardContent>
    </Card>
  );
};

export default GsPlanPanel;
